import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { createFilterOptions } from "@mui/material";
import type { Perimetre } from "models";
import { perimetresService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { multiAutoClassNames, sortingUtil } from "utils";
import { MultiAutoComplete } from "./MultiAutoComplete";
import { PerimetreBox } from "components/Business";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: Perimetre) => {
    const normalizedString = stringUtil.normalize(
      `${option.competence} ${option.code} ${option.libelle}`
    );
    return `${normalizedString} ${option.libelle}`;
  },
});

interface PerimetreMultiAutoProps {
  value: Array<Perimetre>;
  onChange?: (perimetres: Array<Perimetre>) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function PerimetreMultiAuto({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<PerimetreMultiAutoProps>): ReactNode {
  const [perimetres, setPerimetres] = useState<Array<Perimetre>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getPerimetres(): Promise<void> {
      const _perimetres = await perimetresService.getAll();
      _perimetres.sort(sortingUtil.sortPerimetres);
      setPerimetres(_perimetres);
    }

    void catchErrors(getPerimetres);
  }, [catchErrors]);

  return (
    <MultiAutoComplete
      value={value}
      onChange={onChange}
      options={perimetres}
      filterOptions={filterOptions}
      size={size}
      singleItemLabel="Périmètre"
      isLoading={isLoading}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      renderOption={(props, perimetre, { selected }) => (
        <li {...props} key={perimetre.id}>
          <div
            className={`${multiAutoClassNames.AUTOCOMPLETE_CSS_CHECKBOX} ${selected ? multiAutoClassNames.CHECKED : multiAutoClassNames.UNCHECKED}`}
          />
          <PerimetreBox perimetre={perimetre} noWrap />
        </li>
      )}
    />
  );
}

export const PerimetreMultiAutoWithErrorBoundary = withComponentErrorBoundary(PerimetreMultiAuto);
